import { useCallback, useState } from 'react';
import { Show } from '@elfsight/lib-utils';
import { useLingui } from '@lingui/react/macro';
import { CheckoutProvider } from '@stripe/react-stripe-js';
import { useSettings } from '@wu/widget-core';
import type {
  CustomerData,
  PaymentMetadata,
  PaymentProcessingChangeHandler,
  PaymentSuccessHandler,
  ProductData,
  StripeIntegrationSettings,
  TextsDomain
} from '../common';
import { createCheckoutSession, STYLE_DEFAULTS } from '../common';
import {
  CheckoutLoadingContainer,
  CustomerDataBlock,
  PaymentErrorMessage,
  Section
} from './components';
import { WidgetDomainTextsProvider } from './domain-texts';
import {
  SessionMetadataProvider,
  useSessionMetadata
} from './session-metadata-context';
import { StripeCheckoutForm } from './stripe-checkout-form';
import { toStripeAmount, usePaymentAppearances, useStripe } from './utils';

export type PaymentBlockProps = {
  productsData: ProductData[];
  customerData: CustomerData;
  paymentMetadata?: PaymentMetadata;
  onCustomerDataEdit: () => void;
  onPaymentSuccess: PaymentSuccessHandler;
  textsDomain: TextsDomain;
  onPaymentProcessing?: PaymentProcessingChangeHandler;
  color?: string;
  accentColor?: string;
};

export function StripePaymentBlock({
  textsDomain,
  onPaymentProcessing,
  ...forwardingProps
}: PaymentBlockProps) {
  return (
    <SessionMetadataProvider onPaymentProcessing={onPaymentProcessing}>
      <WidgetDomainTextsProvider domain={textsDomain}>
        <StripePaymentBlockInner {...forwardingProps} />
      </WidgetDomainTextsProvider>
    </SessionMetadataProvider>
  );
}

export function StripePaymentBlockInner({
  productsData: rawProductsData,
  customerData,
  paymentMetadata,
  onPaymentSuccess,
  onCustomerDataEdit,
  color = STYLE_DEFAULTS.textColor,
  accentColor = STYLE_DEFAULTS.accentColor
}: Omit<PaymentBlockProps, 'textsDomain' | 'onPaymentProcessing'>) {
  const { t } = useLingui();
  const {
    integrationStripe: { id: integrationId },
    widgetToken
  } = useSettings<StripeIntegrationSettings & { widgetToken?: string }>();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const { setSessionId, isPaymentInProcess } = useSessionMetadata();
  const { appearance, disabledAppearance } = usePaymentAppearances({
    color,
    accentColor
  });

  const stripe = useStripe(() => setError(t`Unable to load Stripe`));

  const fetchClientSecret = useCallback(async () => {
    const unableToCreateSessionError = t`We’re sorry, the payment page isn’t available right now. Please contact us for assistance.`;

    const convertedProductsData: ProductData[] = rawProductsData.map(
      (productData) => {
        return { ...productData, amount: toStripeAmount(productData) };
      }
    );

    try {
      const { clientSecret, sessionId } = await createCheckoutSession({
        providerIntegrationId: integrationId,
        customerEmail: customerData.email,
        productsData: convertedProductsData,
        paymentMetadata,
        widgetToken
      });

      if (clientSecret && sessionId) {
        setSessionId(sessionId);

        return clientSecret;
      }

      setError(unableToCreateSessionError);

      return '';
    } catch (_) {
      setError(unableToCreateSessionError);

      return '';
    }
  }, [integrationId, rawProductsData, customerData.email, t]);

  return (
    <CheckoutLoadingContainer
      isLoading={isLoading && !error}
      textColor={color}
      detailsWithName={!!customerData.name}
    >
      <CustomerDataBlock
        onEdit={onCustomerDataEdit}
        color={color}
        actionColor={accentColor}
        email={customerData.email}
        name={customerData.name}
        isActionDisabled={isPaymentInProcess}
      />

      <CheckoutProvider
        stripe={stripe}
        options={{
          fetchClientSecret,
          elementsOptions: {
            appearance: isPaymentInProcess ? disabledAppearance : appearance,
            savedPaymentMethod: {
              enableSave: 'auto',
              enableRedisplay: 'auto'
            },
            loader: 'always'
          }
        }}
      >
        <StripeCheckoutForm
          onPaymentSuccess={onPaymentSuccess}
          color={color}
          accentColor={accentColor}
          onReady={() => setIsLoading(false)}
        />
      </CheckoutProvider>

      <Show when={!!error}>
        <Section>
          <PaymentErrorMessage>{error}</PaymentErrorMessage>
        </Section>
      </Show>
    </CheckoutLoadingContainer>
  );
}
